import { useEffect, useMemo, useState } from 'react'
import type { CustomerOrder } from '../../customerOrdersApi'
import type { ShopLanguage } from '../../../i18n'
import { buildOrderProgressSteps, toTimestamp } from './dashboardShared'

type UseDashboardOrderSelectionInput = {
  orders: CustomerOrder[]
  language: ShopLanguage
}

export const useDashboardOrderSelection = ({ orders, language }: UseDashboardOrderSelectionInput) => {
  const sortedOrders = useMemo(
    () => [...orders].sort((left, right) => toTimestamp(right.createdAt) - toTimestamp(left.createdAt)),
    [orders],
  )

  const recentOrders = useMemo(() => sortedOrders.slice(0, 6), [sortedOrders])

  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(recentOrders[0]?.id ?? null)

  useEffect(() => {
    if (recentOrders.length === 0) {
      setSelectedOrderId(null)
      return
    }

    setSelectedOrderId((currentValue) =>
      currentValue && recentOrders.some((order) => order.id === currentValue) ? currentValue : recentOrders[0].id,
    )
  }, [recentOrders])

  const selectedOrder = useMemo(
    () => recentOrders.find((order) => order.id === selectedOrderId) ?? null,
    [recentOrders, selectedOrderId],
  )

  const selectedOrderProgress = useMemo(
    () => (selectedOrder ? buildOrderProgressSteps(selectedOrder, language) : []),
    [language, selectedOrder],
  )

  const selectedOrderNextStep = useMemo(
    () => selectedOrderProgress.find((step) => step.tone === 'upcoming') ?? null,
    [selectedOrderProgress],
  )

  return {
    recentOrders,
    selectedOrderId,
    setSelectedOrderId,
    selectedOrder,
    selectedOrderProgress,
    selectedOrderNextStep,
  }
}
